import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';
import Loader from '../../../components/Loader';
import Paginator from '../../../components/Paginator';
import moment from 'moment';
import { hasPermission } from '../../../utils/permissions';

const NotificationsList = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: ''
  });
  // What is in the box, which is not yet what has been searched for — see the
  // debounce below.
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    getNotifications();
  }, [filters]);

  // Wait for a pause in typing before querying: a request per keystroke also
  // means responses can land out of order and show results for an earlier term.
  useEffect(() => {
    if (searchInput === filters.search) return;
    const timer = setTimeout(() => handleFilterChange('search', searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput, filters.search]);

  const getNotifications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await ApiService.request({
        method: 'GET',
        url: `admin/getNotifications?${params.toString()}`
      });

      if (response.data.status) {
        const payload = response.data.data || {};
        setNotifications(payload.notifications || []);
        if (payload.pagination) {
          setPagination(payload.pagination);
        }
      }
    } catch (error) {
      toast.error('Error fetching notifications');
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * The payload the backend attaches, e.g. { type: 'booking', booking_id }.
   * The schema types `data` as an Array, so Mongoose wraps the object it is
   * given — read either shape rather than depending on that.
   */
  const payloadOf = (notification) => {
    const data = notification?.data;
    if (Array.isArray(data)) return data[0] || {};
    return data || {};
  };

  /** Where a notification points, or null when it isn't about anything. */
  const targetOf = (notification) => {
    const { type, booking_id, membership_id, user_id, cancellation } = payloadOf(notification);

    if (booking_id) return `/admin/bookings/${booking_id}`;
    if (membership_id && cancellation) return '/admin/membership-plans/cancellation-requests';
    if (membership_id || type === 'membership') return '/admin/membership-plans/subscribers';
    if (user_id) return `/admin/users/${user_id}`;
    return null;
  };

  const handleNotificationClick = (notification) => {
    const target = targetOf(notification);
    if (target) navigate(target);
  };

  const markAsRead = async (id) => {
    try {
      await ApiService.request({
        method: 'POST',
        url: 'admin/markNotificationRead',
        data: { id },
      });
      setNotifications(prev =>
        prev.map(n => (n._id === id ? { ...n, is_read: true } : n))
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleRowClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification._id);
    }
    handleNotificationClick(notification);
  };

  const markAllAsRead = async () => {
    try {
      const response = await ApiService.request({
        method: 'POST',
        url: 'admin/markAllNotificationsRead',
      });
      if (response.data.status) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const clearAllNotifications = async () => {
    try {
      setLoading(true);
      const response = await ApiService.request({
        method: "POST",
        url: "admin/clearAllNotifications",
      });
      const data = response.data;
      if (data.status) {
        setNotifications([]);
        toast.success('All notifications cleared successfully');
        // Refresh the list
        getNotifications();
      } else {
        toast.error(data.message || 'Failed to clear notifications');
      }
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast.error('Failed to clear notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      // Changing a filter returns to the first page; changing the page itself
      // obviously must not.
      ...(key === 'page' ? null : { page: 1 }),
    }));
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Toolbar */}
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              Notifications
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <a href="/dashboard" className="text-muted text-hover-primary">Dashboard</a>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">Notifications</li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 gap-lg-3">
            {notifications.some(n => !n.is_read) && (
              <button
                className="btn btn-light btn-sm"
                onClick={markAllAsRead}
                disabled={loading}
              >
                <i className="bi bi-check2-all me-2"></i>Mark all as read
              </button>
            )}
            {notifications.length > 0 && hasPermission('notifications.clear') && (
              <button
                className="btn btn-base btn-sm"
                onClick={clearAllNotifications}
                disabled={loading}
              >
                <i className="bi bi-trash me-2"></i>Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <h3>All Notifications</h3>
              </div>
              <div className="card-toolbar">
                <div className="d-flex align-items-center position-relative">
                  <i className="bi bi-search position-absolute ms-4" style={{ zIndex: 10 }}></i>
                  <input
                    type="text"
                    className="form-control form-control-solid w-250px ps-13"
                    placeholder="Search notifications..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              {loading ? (
                <Loader />
              ) : (
                <>
                  {notifications.length > 0 ? (
                    <>
                      <div className="list-group list-group-flush">
                        {notifications.map((notification, index) => {
                          const isUnread = !notification.is_read;
                          const target = targetOf(notification);
                          const { type } = payloadOf(notification);
                          const clickable = isUnread || Boolean(target);
                          const baseBg = isUnread ? '#eef3ff' : 'transparent';
                          return (
                          <div
                            key={notification._id || index}
                            className={`list-group-item list-group-item-action border-bottom ${
                              clickable ? 'cursor-pointer' : ''
                            }`}
                            style={{
                              cursor: clickable ? 'pointer' : 'default',
                              transition: 'background-color 0.2s',
                              backgroundColor: baseBg,
                              borderLeft: isUnread ? '3px solid #2f80ed' : '3px solid transparent',
                            }}
                            onClick={() => {
                              if (clickable) handleRowClick(notification);
                            }}
                            onMouseEnter={(e) => {
                              if (clickable) e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = baseBg;
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center mb-2">
                                  {isUnread && (
                                    <span
                                      title="Unread"
                                      style={{
                                        display: 'inline-block',
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: '#2f80ed',
                                        marginRight: 8,
                                        flexShrink: 0,
                                      }}
                                    ></span>
                                  )}
                                  <span className={`text-dark fs-6 me-2 ${isUnread ? 'fw-bold' : 'fw-semibold'}`}>{notification.title}</span>
                                  {type && (
                                    <span className="badge badge-light-primary text-capitalize">
                                      {String(type).replace('_', ' ')}
                                    </span>
                                  )}
                                </div>
                                <p className="text-gray-700 mb-1" style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                                  {notification.message}
                                </p>
                                {target && (
                                  <small className="text-primary">
                                    Click to view details <i className="bi bi-arrow-right"></i>
                                  </small>
                                )}
                              </div>
                              <div className="ms-3 text-end">
                                <small className="text-muted d-block">
                                  {moment(notification.createdAt).format('MMM DD, YYYY')}
                                </small>
                                <small className="text-muted">
                                  {moment(notification.createdAt).fromNow()}
                                </small>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                      {pagination.total_pages > 1 && (
                        <div className="d-flex justify-content-center mt-5">
                          <Paginator
                            page={pagination.current_page || filters.page}
                            rows={pagination.per_page || filters.limit}
                            totalRecords={pagination.total_records || 0}
                            onPageChange={(page) => handleFilterChange('page', page)}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-10">
                      <i className="bi bi-bell-slash fs-3x text-gray-400 mb-5"></i>
                      {filters.search ? (
                        <>
                          <h3 className="text-gray-600 mb-2">No Matches</h3>
                          <p className="text-gray-500">
                            No notifications match &ldquo;{filters.search}&rdquo;.
                          </p>
                        </>
                      ) : (
                        <>
                          <h3 className="text-gray-600 mb-2">No Notifications</h3>
                          <p className="text-gray-500">You don't have any notifications at the moment.</p>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsList;

